import { useEffect, useRef } from 'react'
import type { ReactNode } from 'react'
import styles from './Modal.module.css'

interface ModalProps {
    open: boolean
    title: string
    onClose: () => void
    children: ReactNode
}

/**
 * Native <dialog>, which gives focus trapping, Escape handling and inertness of
 * the rest of the page for free. The old modals were divs with a hardcoded
 * aria-hidden="true" that stayed true while they were visible, no focus trap and
 * no Escape key.
 */
export default function Modal({ open, title, onClose, children }: ModalProps) {
    const ref = useRef<HTMLDialogElement>(null)

    useEffect(() => {
        const el = ref.current
        if (!el) return
        if (open && !el.open) el.showModal()
        else if (!open && el.open) el.close()
    }, [open])

    // <dialog> fires close for Escape as well as close(); keep React state in step
    useEffect(() => {
        const el = ref.current
        if (!el) return
        const handler = () => onClose()
        el.addEventListener('close', handler)
        return () => el.removeEventListener('close', handler)
    }, [onClose])

    return (
        <dialog ref={ref} className={styles.dialog} aria-labelledby="modal-title">
            <div className={styles.head}>
                <h2 id="modal-title" className={styles.title}>{title}</h2>
                <button type="button" className={styles.close} onClick={onClose} aria-label="Close">
                    ✕
                </button>
            </div>
            <div className={styles.body}>{children}</div>
        </dialog>
    )
}
